"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Alert,
  Paper,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import {
  Close as CloseIcon,
  FilterAlt as FilterAltIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  CompareArrows as CompareArrowsIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PlayArrowIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon, // New icon for .doc download
  Phone as PhoneIcon, // For call button icon
  Link as LinkIcon, // For link button icon
  Stop as StopIcon, // New icon for Finish Campaign
} from "@mui/icons-material"
import { API_URL } from "../../constants"
import { toast } from "react-toastify"
import { skipComparison, compareCampaigns, runCampaign } from "../../slices/campaignSlice"
import CompareCampaignsDialog from "./CompareCampaignsDialog"

// Helper to parse "AM/PM" time strings into 24-hour format (e.g., "08:00" for 8 AM, "18:00" for 6 PM)
const parseTime = (timeStr) => {
  const parts = timeStr.toUpperCase().split(" ")
  const timePart = parts[0]
  const period = parts[1]

  let hours, minutes

  // Handle HH:MM or H.MM format
  if (timePart.includes(":")) {
    ;[hours, minutes] = timePart.split(":").map(Number)
  } else if (timePart.includes(".")) {
    ;[hours, minutes] = timePart.split(".").map(Number)
  } else {
    hours = Number(timePart)
    minutes = 0
  }

  // Adjust hours for AM/PM
  if (period === "PM" && hours !== 12) {
    hours += 12
  } else if (period === "AM" && hours === 12) {
    hours = 0
  }

  return { hours: isNaN(hours) ? 0 : hours, minutes: isNaN(minutes) ? 0 : minutes }
}

// Helper to parse days like "Monday - Saturday" or "Sunday, Monday"
const parseDays = (daysStr) => {
  const daysMap = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  }
  const allowedDays = new Set()

  if (daysStr.includes("-")) {
    const [startDay, endDay] = daysStr.split("-").map((s) => s.trim().toLowerCase())
    const startIndex = daysMap[startDay]
    const endIndex = daysMap[endDay]

    // Handle wrap around (e.g., Friday - Monday)
    if (startIndex > endIndex) {
      for (let i = startIndex; i <= 6; i++) allowedDays.add(i)
      for (let i = 0; i <= endIndex; i++) allowedDays.add(i)
    } else {
      // Normal range (e.g., Monday - Saturday)
      for (let i = startIndex; i <= endIndex; i++) allowedDays.add(i)
    }
  } else {
    // Comma-separated days
    daysStr.split(",").map((s) => {
      const dayIndex = daysMap[s.trim().toLowerCase()]
      if (dayIndex !== undefined) allowedDays.add(dayIndex)
    })
  }
  return allowedDays
}

// Helper to format 24-hour to 12-hour for display
const formatHour12 = (h) => {
  const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h
  return hour12.toString()
}

// Helper to parse phone number and extract country code (client-side replica)
const parsePhoneNumberClient = (fullNumber, defaultCountryCode) => {
  let cleanedNumber = fullNumber.trim()
  if (cleanedNumber.startsWith("+")) {
    cleanedNumber = "+" + cleanedNumber.substring(1).replace(/[^\d]/g, "")
  } else {
    cleanedNumber = cleanedNumber.replace(/[^\d]/g, "")
  }

  let countryCode = defaultCountryCode
  let number = cleanedNumber

  if (cleanedNumber.startsWith("+")) {
    const countryCodeMatch = cleanedNumber.match(/^\+(\d{1,3})/) // Allow 1 to 3 digits for country code
    if (countryCodeMatch && countryCodeMatch[1]) {
      countryCode = "+" + countryCodeMatch[1]
      number = cleanedNumber.substring(countryCode.length)
    }
  }
  return { countryCode, number }
}

// Helper to normalize phone numbers for consistent lookup in sets (client-side replica)
const normalizePhoneNumberClient = (fullNumber, defaultCampaignCountryCode) => {
  const { countryCode, number } = parsePhoneNumberClient(fullNumber, defaultCampaignCountryCode)
  return `${countryCode}${number}`
}

// CampaignDetailsDialog Component
function CampaignDetailsDialog({
  open,
  onClose,
  campaign,
  userInfo, // Passed from useAuth in MyCampaignsPage or directly in PendingCampaignPage
  fetchCampaigns, // Function to refresh campaign list in parent component
  showProcessingTimeline, // NEW PROP: boolean to control visibility of processing section
  // Props below are only relevant when showProcessingTimeline is true (i.e., from PendingCampaignPage)
  onOpenFindNonWhatsAppDialog,
  onSkipFiltering,
  onComplete,
  onReject,
  onResetCampaign,
  isSubmitting, // This is the isSubmitting from PendingCampaignPage
  checkValidationStatus,
}) {
  const dispatch = useDispatch()
  const { loading: campaignLoading } = useSelector((state) => state.campaigns)
  const [validationStatus, setValidationStatus] = useState(null)
  const [validationPolling, setValidationPolling] = useState(false)

  // State for run campaign dialog
  const [runCampaignDialogOpen, setRunCampaignDialogOpen] = useState(false)
  const [servers, setServers] = useState([])
  const [selectedServerId, setSelectedServerId] = useState("")
  const [loadingServers, setLoadingServers] = useState(false)
  const [runningCampaignAction, setRunningCampaignAction] = useState(false) // Renamed from runningCampaign for clarity with isRunning prop
  const [downloadLink, setDownloadLink] = useState(null)
  const [globalWhitelistedNumbers, setGlobalWhitelistedNumbers] = useState(new Set())

  // New state for compare campaigns dialog
  const [compareCampaignDialogOpen, setCompareCampaignDialogOpen] = useState(false)
  const [isComparing, setIsComparing] = useState(false)

  // New states for Run Campaign options
  const [runType, setRunType] = useState("all")
  const [cuttingPercentage, setCuttingPercentage] = useState(0)

  // State variables for media preview
  const [mediaPreviewDialogOpen, setMediaPreviewDialogOpen] = useState(false)
  const [mediaPreviewContent, setMediaPreviewContent] = useState(null)

  // State for campaign configurations (specifically running message)
  const [campaignConfigs, setCampaignConfigs] = useState(null)
  const [loadingConfigs, setLoadingConfigs] = useState(true)
  const [runningTimeConstraints, setRunningTimeConstraints] = useState(null)

  // NEW: State for Finish Campaign confirmation dialog
  const [finishCampaignConfirmOpen, setFinishCampaignConfirmOpen] = useState(false)
  const [isFinishingCampaign, setIsFinishingCampaign] = useState(false)

  const [commonNumbersList, setCommonNumbersList] = useState([])
  const [whitelistedNumbersList, setWhitelistedNumbersList] = useState([])
  const [whitelistedWithoutCommonNumbersList, setWhitelistedWithoutCommonNumbersList] = useState([])

  // Fetch campaign configurations on component mount
  useEffect(() => {
    const fetchConfigs = async () => {
      setLoadingConfigs(true)
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
        const response = await fetch(`${API_URL}/api/campaign-config`, config)
        if (!response.ok) {
          throw new Error("Failed to fetch campaign configurations")
        }
        const data = await response.json()
        setCampaignConfigs(data)

        const runningMessageConfig = data.find((c) => c.name === "Running Message")
        if (runningMessageConfig && runningMessageConfig.value) {
          const message = runningMessageConfig.value
          const match = message.match(
            /between\s*(\d{1,2}(?:\.\d{2})?\s*[AP]M)\s*to\s*(\d{1,2}(?:\.\d{2})?\s*[AP]M),\s*(.+?)\.?\s*$/i,
          )
          if (match) {
            const [, startTimeStr, endTimeStr, daysStr] = match
            const startTime = parseTime(startTimeStr)
            const endTime = parseTime(endTimeStr)
            const allowedDays = parseDays(daysStr)
            setRunningTimeConstraints({ startTime, endTime, allowedDays })
          } else {
            console.warn("Running message format not recognized:", message)
            setRunningTimeConstraints(null)
          }
        } else {
          setRunningTimeConstraints(null)
        }
      } catch (error) {
        console.error("Error fetching campaign configs:", error)
        toast.error("Failed to load campaign operating hours.")
      } finally {
        setLoadingConfigs(false)
      }
    }

    if (open && userInfo?.token) {
      fetchConfigs()
    }
  }, [open, userInfo?.token])

  // Fetch global whitelisted numbers on component mount
  // useEffect(() => {
  //   const fetchWhitelistedNumbers = async () => {
  //     if (!userInfo?.token) return
  //     try {
  //       const config = {
  //         headers: {
  //           Authorization: `Bearer ${userInfo.token}`,
  //         },
  //       }
  //       const response = await fetch(`${API_URL}/api/white-listing/numbers`, config)
  //       if (!response.ok) {
  //         throw new Error("Failed to fetch whitelisted numbers")
  //       }
  //       const data = await response.json()
  //       setGlobalWhitelistedNumbers(new Set(data.whitelistedNumbers))
  //     } catch (error) {
  //       console.error("Error fetching whitelisted numbers:", error)
  //       toast.error("Failed to load whitelisted numbers.")
  //     }
  //   }

  //   if (open && userInfo?.token && userInfo?.user?.role === "admin") {
  //     fetchWhitelistedNumbers()
  //   }
  // }, [open, userInfo?.token])

  // Effect to generate download link for common numbers
  // useEffect(() => {
  //   if (
  //     campaign &&
  //     Array.isArray(campaign.commonNumbersList) &&
  //     campaign.commonNumbersList.length > 0 &&
  //     campaign.comparisonStatus === "completed" &&
  //     globalWhitelistedNumbers.size > 0 // Ensure whitelist data is loaded
  //   ) {
  //     const campaignCountryCode = campaign.countryCode || "+91"
  //     const csvRows = []
  //     csvRows.push("Number,isWhiteList") // Header

  //     campaign.commonNumbersList.forEach((number) => {
  //       const normalizedNum = normalizePhoneNumberClient(number, campaignCountryCode)
  //       const isWhitelisted = globalWhitelistedNumbers.has(normalizedNum)
  //       csvRows.push(`${number},${isWhitelisted ? "TRUE" : "FALSE"}`)
  //     })

  //     const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n")
  //     const encodedUri = encodeURI(csvContent)
  //     setDownloadLink(encodedUri)
  //   } else {
  //     setDownloadLink(null)
  //   }
  // }, [campaign, globalWhitelistedNumbers])


  // Helper function to check if current time is within operating hours
  const isWithinOperatingHours = () => {
    // If no constraints are set, or if not an admin, always allow
    if (!runningTimeConstraints || userInfo?.user?.role !== "admin") {
      return { allowed: true, message: "" }
    }

    const { startTime, endTime, allowedDays } = runningTimeConstraints
    const now = new Date()
    const currentDay = now.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const currentHours = now.getHours()
    const currentMinutes = now.getMinutes()

    const formattedStartTime = `${formatHour12(startTime.hours)}:${startTime.minutes.toString().padStart(2, "0")} ${startTime.hours >= 12 ? "PM" : "AM"}`
    const formattedEndTime = `${formatHour12(endTime.hours)}:${endTime.minutes.toString().padStart(2, "0")} ${endTime.hours >= 12 ? "PM" : "AM"}`
    const daysArray = Array.from(allowedDays)
      .sort((a, b) => a - b)
      .map((dayIndex) => ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dayIndex])
    const daysMessage =
      daysArray.length > 2 ? `${daysArray[0]} - ${daysArray[daysArray.length - 1]}` : daysArray.join(", ")

    if (!allowedDays.has(currentDay)) {
      return {
        allowed: false,
        message: `Campaigns can only be run between ${formattedStartTime} to ${formattedEndTime}, ${daysMessage}.`,
      }
    }

    // Convert current time to minutes for easy comparison
    const currentTotalMinutes = currentHours * 60 + currentMinutes
    const startTotalMinutes = startTime.hours * 60 + startTime.minutes
    const endTotalMinutes = endTime.hours * 60 + endTime.minutes

    if (startTotalMinutes < endTotalMinutes) {
      // Normal range (e.g., 8 AM to 6 PM)
      if (currentTotalMinutes >= startTotalMinutes && currentTotalMinutes <= endTotalMinutes) {
        return { allowed: true, message: "" }
      }
    } else {
      // Overnight range (e.g., 10 PM to 6 AM)
      if (currentTotalMinutes >= startTotalMinutes || currentTotalMinutes <= endTotalMinutes) {
        return { allowed: true, message: "" }
      }
    }

    return {
      allowed: false,
      message: `Campaigns can only be run between ${formattedStartTime} to ${formattedEndTime}, ${daysMessage}.`,
    }
  }

  // Helper function for media URL
  const getMediaUrl = (media) => {
    if (!media) return null
    // Assuming media.url already contains the path like `/uploads/filename.jpg`
    // Ensure it's a full URL if it's not already
    if (media.url && (media.url.startsWith("http://") || media.url.startsWith("https://"))) {
      return media.url
    }
    return `${API_URL}${media.url}`
  }

  // Helper functions for media preview
  const handleOpenMediaPreview = (mediaItem) => {
    setMediaPreviewContent(mediaItem)
    setMediaPreviewDialogOpen(true)
  }

  const handleCloseMediaPreview = () => {
    setMediaPreviewDialogOpen(false)
    setMediaPreviewContent(null)
  }

  // NEW: Function to handle downloading media files
  const handleDownloadMedia = async (mediaItem) => {
    const mediaUrl = getMediaUrl(mediaItem)
    if (!mediaUrl) {
      toast.error("Media URL not available.")
      return
    }

    try {
      const response = await fetch(mediaUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch media: ${response.statusText}`)
      }
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = mediaItem.name || "download" // Use original file name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      toast.success(`Downloading ${mediaItem.name}...`)
    } catch (error) {
      console.error("Error downloading media:", error)
      toast.error(`Failed to download media: ${error.message}`)
    }
  }

  // Fetch validation status if available (for admin-specific flow)
  useEffect(() => {
    if (showProcessingTimeline && campaign && campaign.validationBatchId && userInfo?.token && checkValidationStatus) {
      const checkStatus = async () => {
        const status = await checkValidationStatus(campaign._id, campaign.validationBatchId)
        if (status) {
          setValidationStatus(status.status)

          // If validation is completed or has error, stop polling
          if (status.status?.status === "completed" || status.status?.status === "error") {
            setValidationPolling(false)
          }
        }
      }

      checkStatus()

      // Poll for updates if validation is in progress
      if (
        validationPolling ||
        campaign.filteringStatus === "step1_in_progress" ||
        campaign.filteringStatus === "step2_in_progress" ||
        campaign.filteringStatus === "step3_in_progress"
      ) {
        const interval = setInterval(checkStatus, 5000)
        return () => clearInterval(interval)
      }
    }
  }, [campaign, validationPolling, isComparing, userInfo?.token, showProcessingTimeline, checkValidationStatus])

  // Function to fetch available servers
  const fetchServers = async () => {
    setLoadingServers(true)
    try {
      const response = await fetch(`${API_URL}/api/campaigns/filter/servers`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch servers")
      }

      const data = await response.json()
      const serversList = data.servers || []
      setServers(serversList)

      // Set the filtering server as default if available
      if (campaign && campaign.filteringServer) {
        const filteringServerId =
          typeof campaign.filteringServer === "object" ? campaign.filteringServer._id : campaign.filteringServer

        // Check if the filtering server is in the list
        const serverExists = serversList.some((server) => server._id === filteringServerId)

        if (serverExists) {
          setSelectedServerId(filteringServerId)
        } else if (serversList.length > 0) {
          // If filtering server is not available, select the first one
          setSelectedServerId(serversList[0]._id)
        }
      } else if (serversList.length > 0) {
        // If no filtering server, select the first one
        setSelectedServerId(serversList[0]._id)
      }
    } catch (error) {
      console.error("Error fetching servers:", error)
    } finally {
      setLoadingServers(false)
    }
  }

  // Function to handle running the campaign
  const handleRunCampaign = async () => {
    if (!campaign || !selectedServerId) {
      toast.error("Please select a server to run the campaign.")
      return
    }

    setRunningCampaignAction(true)
    try {
      const resultAction = await dispatch(
        runCampaign({
          id: campaign._id,
          serverId: selectedServerId,
          runType,
          cuttingPercentage,
        }),
      )

      if (runCampaign.fulfilled.match(resultAction)) {
        setRunCampaignDialogOpen(false)
        toast.success(resultAction.payload.message || "Campaign is now running!")
        if (onClose) {
          onClose()
        }
        if (fetchCampaigns) {
          // Refresh campaigns in parent after running
          fetchCampaigns()
        }
      } else {
        throw new Error(resultAction.payload || resultAction.error.message || "Failed to run campaign")
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setRunningCampaignAction(false)
    }
  }

  // Function to handle opening the run campaign dialog
  const handleOpenRunCampaignDialog = () => {
    const { allowed, message } = isWithinOperatingHours()
    if (!allowed) {
      //toast.error(message)
      //return
    }
    fetchServers()
    setRunCampaignDialogOpen(true)
    setRunType("all")
    setCuttingPercentage(0)
  }

  // Function to handle closing the run campaign dialog
  const handleCloseRunCampaignDialog = () => {
    setRunCampaignDialogOpen(false)
  }

  // Function to handle opening the compare campaigns dialog
  const handleOpenCompareCampaignsDialog = () => {
    setCompareCampaignDialogOpen(true)
  }

  // Function to handle closing the compare campaigns dialog
  const handleCloseCompareCampaignsDialog = () => {
    setCompareCampaignDialogOpen(false)
  }

  // Function to handle initiating campaign comparison
  const handleCompareCampaigns = async (selectedCampaignIds) => {
    if (!campaign || selectedCampaignIds.length === 0) {
      toast.error("Please select at least one campaign to compare.")
      return
    }

    setIsComparing(true)
    try {
      const resultAction = await dispatch(
        compareCampaigns({
          currentCampaignId: campaign._id,
          previousCampaignIds: selectedCampaignIds,
          currentCampaignUserId: campaign.userId._id || campaign.userId,
        }),
      )

      if (compareCampaigns.fulfilled.match(resultAction)) {
        toast.success(resultAction.payload.message || "Comparison successful!")
        if (fetchCampaigns) {
          // Refresh campaigns in parent after comparison
          fetchCampaigns()
        }
      } else {
        throw new Error(resultAction.error.message || "Failed to compare campaigns")
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setIsComparing(false)
      handleCloseCompareCampaignsDialog()
    }
  }

  // Function to handle skipping campaign comparison
  const handleSkipComparison = async () => {
    if (!campaign) return

    setIsComparing(true)
    try {
      const resultAction = await dispatch(skipComparison(campaign._id))
      if (skipComparison.fulfilled.match(resultAction)) {
        toast.info(resultAction.payload.message || "Campaign comparison skipped.")
        setDownloadLink(null)
        if (fetchCampaigns) {
          // Refresh campaigns in parent after skipping
          fetchCampaigns()
        }
      } else {
        throw new Error(resultAction.error.message || "Failed to skip comparison")
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setIsComparing(false)
    }
  }

  // NEW: Function to handle opening Finish Campaign confirmation dialog
  const handleOpenFinishCampaignConfirm = () => {
    setFinishCampaignConfirmOpen(true)
  }

  // NEW: Function to handle closing Finish Campaign confirmation dialog
  const handleCloseFinishCampaignConfirm = () => {
    setFinishCampaignConfirmOpen(false)
  }

  // NEW: Function to confirm and finish the campaign
  const confirmFinishCampaign = async () => {
    if (!campaign || !userInfo?.token) {
      toast.error("Campaign or user information missing.")
      return
    }

    setIsFinishingCampaign(true)
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaign._id}/finish`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to finish campaign.")
      }

      const data = await response.json()
      toast.success(data.message || "Campaign finished successfully!")
      handleCloseFinishCampaignConfirm()
      if (onClose) {
        onClose() // Close the details dialog
      }
      if (fetchCampaigns) {
        fetchCampaigns() // Refresh campaign list in parent
      }
    } catch (error) {
      console.error("Error finishing campaign:", error)
      toast.error(`Error: ${error.message}`)
    } finally {
      setIsFinishingCampaign(false)
    }
  }

  // NEW: Function to handle downloading specific number lists
  const downloadNumbersAsCsv = (numbers, filename) => {
    if (!numbers || numbers.length === 0) {
      toast.info(`No numbers available for ${filename.replace(".csv", "")}.`)
      return
    }
    const csvContent = "data:text/csv;charset=utf-8," + numbers.join("\n")
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement("a")
    link.setAttribute("href", encodedUri)
    link.setAttribute("download", filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success(`${filename} downloaded successfully!`)
  }

  const downloadNumbersArrayAsCsv = (rows, filename) => {
  if (!rows || rows.length === 0) {
    toast.info(`No numbers available for ${filename.replace(".csv", "")}.`)
    return
  }

  // Convert array of arrays to CSV string
  const csvContent =
    "data:text/csv;charset=utf-8," +
    rows.map((row) => row.join(",")).join("\n")

  const encodedUri = encodeURI(csvContent)
  const link = document.createElement("a")
  link.setAttribute("href", encodedUri)
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  toast.success(`${filename} downloaded successfully!`)
}


  const handleDownloadTotalNumbers = () => {
    downloadNumbersAsCsv(campaign.originalNumbers, `total_numbers_${campaign.title.replace(/\s+/g, "_").toLowerCase()}.csv`)
  }

  const handleDownloadNonWhatsAppNumbers = () => {
    downloadNumbersAsCsv(
      campaign.nonWhatsAppNumbers,
      `non_whatsapp_numbers_${campaign.title.replace(/\s+/g, "_").toLowerCase()}.csv`,
    )
  }

  const handleDownloadWhatsAppNumbers = () => {
    const campaignCountryCode = campaign.countryCode || "+91"
    const nonWhatsAppSet = new Set(
      campaign.nonWhatsAppNumbers?.map((num) => normalizePhoneNumberClient(num, campaignCountryCode)) || [],
    )
    const whatsAppNumbers = campaign.numbers.filter(
      (num) => !nonWhatsAppSet.has(normalizePhoneNumberClient(num, campaignCountryCode)),
    )
    downloadNumbersAsCsv(whatsAppNumbers, `whatsapp_numbers_${campaign.title.replace(/\s+/g, "_").toLowerCase()}.csv`)
  }

  const handleDownloadWhitelistedNumbers = () => {

    downloadNumbersAsCsv(
      whitelistedNumbersList,
      `whitelisted_numbers_${campaign.title.replace(/\s+/g, "_").toLowerCase()}.csv`,
    )
  }
  const handleDownloadWhitelistedAndCommonNumbers = () => {
    downloadNumbersArrayAsCsv(
      campaign?.initialReportRows,
      `whitelisted_numbers_${campaign.title.replace(/\s+/g, "_").toLowerCase()}.csv`,
    )
  }


  const handleDownloadCommonNumbers = () => {
    downloadNumbersAsCsv(
      commonNumbersList,
      `common_numbers_${campaign.title.replace(/\s+/g, "_").toLowerCase()}.csv`,
    )
  }

  const handleDownloadWhiteListWithoutCommonNumbers = () => {

    downloadNumbersAsCsv(
      whitelistedWithoutCommonNumbersList,
      `whitelisted_numbers_without_common_${campaign.title.replace(/\s+/g, "_").toLowerCase()}.csv`
    );
  };


  // useEffect(() => {
    
  // if(campaign && campaign.numbers && globalWhitelistedNumbers){
  //   // Update whitelistedNumbersList and commonNumbersList when campaign or globalWhitelistedNumbers change
  //   const campaignCountryCode = campaign?.countryCode || "+91"
  //   const whitelistedInCampaign = campaign?.numbers.filter((num) =>
  //     globalWhitelistedNumbers.has(normalizePhoneNumberClient(num, campaignCountryCode)),
  //   )
  //   setWhitelistedNumbersList(whitelistedInCampaign);

  //   const normalizedCommonNumbers = campaign?.commonNumbersList.map((num) =>
  //     normalizePhoneNumberClient(num, campaignCountryCode)
  //   );
  //   setCommonNumbersList(normalizedCommonNumbers);

  //   const whitelistWithoutCommon = whitelistedInCampaign.filter(
  //     (num) => !normalizedCommonNumbers.includes(normalizePhoneNumberClient(num, campaignCountryCode))
  //   );

  //   setWhitelistedWithoutCommonNumbersList(whitelistWithoutCommon);

  // }

  // }, [ globalWhitelistedNumbers])


  if (!campaign) return null

  // Check if campaign is rejected
  const isRejected = campaign.status === "rejected"

  // Determine if the Find non-WhatsApp step is completed
  const isFindNonWhatsAppCompleted =
    validationStatus?.status === "completed" || campaign.filteringStatus === "step3_completed"

  // Determine if comparison step is completed or skipped
  const isComparisonCompletedOrSkipped =
    campaign.comparisonStatus === "completed" || campaign.comparisonStatus === "skipped"

  // Determine if validation is in progress
  const isValidationInProgress =
    (campaign?.validationBatchId && validationStatus?.status === "processing") ||
    campaign.filteringStatus === "step3_in_progress"

  // Check if we should show the Filter and Skip buttons for "Find non-WhatsApp" (Admin specific)
  const shouldShowFilterButtons =
    userInfo?.user?.role === "admin" &&
    !isRejected &&
    !campaign.validationBatchId && // No validation batch initiated yet
    campaign.filteringStatus === "step2_completed" && // Previous step completed
    campaign.filteringStatus !== "step3_in_progress" && // Not already in progress
    campaign.filteringStatus !== "step3_completed" && // Not already completed
    !(validationStatus && validationStatus.status === "completed") // Not completed via validation check

  // Check if we should show the Compare and Skip buttons for "Compare Campaigns" (Admin specific)
  const shouldShowCompareButtons =
    userInfo?.user?.role === "admin" &&
    !isRejected &&
    isFindNonWhatsAppCompleted &&
    (campaign.comparisonStatus === "not_started" || campaign.comparisonStatus === "error") &&
    (campaign.status === "pending" || campaign.status === "processing")

  // Check if we should show the Run Campaign button (Admin specific)
  const canRunCampaign =
    userInfo?.user?.role === "admin" &&
    campaign.status === "processing" &&
    !campaign.isRunning &&
    (campaign.filteringStatus === "step3_completed" || (validationStatus && validationStatus.status === "completed")) &&
    isComparisonCompletedOrSkipped

  // The run button itself should only be disabled by runningCampaignAction state or isSubmitting from parent
  const shouldDisableRunButton = runningCampaignAction || isSubmitting || campaign.isRunning

  // NEW: Check if we should show the Finish Campaign button (Admin specific)
  const canFinishCampaign =
    userInfo?.user?.role === "admin" &&
    (campaign.status === "processing" ||
      (campaign.status === "pending" &&
        campaign.isWhitelistCampaign &&
        campaign.campaignType !== "button" &&
        Boolean(campaign.channelWaitReason)))
  // Get server name from campaign or validation status
  const getServerName = () => {
    if (validationStatus?.serverInfo?.name) {
      return validationStatus.serverInfo.name
    }
    if (campaign.filteringServer && typeof campaign.filteringServer === "object" && campaign.filteringServer.name) {
      return campaign.filteringServer.name
    }
    if (campaign.serverName) {
      return campaign.serverName
    }
    return null
  }

  const serverName = getServerName()

  // Function to handle downloading message as .doc
  const handleDownloadMessageAsDoc = () => {
    let htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
    <meta charset="UTF-8">
    <title>Campaign Message - ${campaign.title}</title>
    <style>
      body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 20px; }
      .container { max-width: 800px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
      h1 { color: #0056b3; border-bottom: 2px solid #eee; padding-bottom: 10px; margin-bottom: 20px; }
      h2 { color: #0056b3; margin-top: 25px; }
      p { margin-bottom: 15px; }
      img { max-width: 100%; height: auto; display: block; margin: 15px 0; border: 1px solid #eee; padding: 5px; }
      .media-link { display: block; margin: 10px 0; }
      .media-link a { text-decoration: none; color: #1976d2; font-weight: bold; }
      .media-link a:hover { text-decoration: underline; }
    </style>
    </head>
    <body>
      <div class="container">
        <h1>Campaign: ${campaign.title || "Untitled Campaign"}</h1>
        <h2>Message:</h2>
        <p style="white-space: pre-wrap;">${campaign.message || "No message content"}</p>
  `

    if (campaign.media && campaign.media.length > 0) {
      htmlContent += `<h2>Attached Media:</h2>`
      campaign.media.forEach((mediaItem) => {
        const mediaUrl = getMediaUrl(mediaItem) // Use the existing helper
        const fileName = mediaItem.name || "File"
        const isImage = mediaItem.type?.startsWith("image/") || fileName.match(/\.(jpg|jpeg|png|gif|bmp)$/i)
        const isVideo = mediaItem.type?.startsWith("video/") || fileName.match(/\.(mp4|webm|ogg)$/i.test(fileName))
        const isPdf = mediaItem.type === "application/pdf" || fileName.match(/\.pdf$/i)

        if (isImage) {
          htmlContent += `<img src="${mediaUrl}" alt="${fileName}"><br/>`
        } else if (isVideo) {
          htmlContent += `<div class="media-link">Video: <a href="${mediaUrl}" target="_blank">${fileName}</a> (Cannot embed video directly in .doc HTML)</div>`
        } else if (isPdf) {
          htmlContent += `<div class="media-link">PDF: <a href="${mediaUrl}" target="_blank">${fileName}</a></div>`
        } else {
          htmlContent += `<div class="media-link">File: <a href="${mediaUrl}" target="_blank">${fileName}</a></div>`
        }
      })
    }

    if (campaign.campaignType === "button" && campaign.buttons && campaign.buttons.length > 0) {
      htmlContent += `<h2>Buttons:</h2><ul>`
      campaign.buttons.forEach((button) => {
        htmlContent += `<li><strong>${button.text}</strong>: `
        if (button.type === "link") {
          htmlContent += `<a href="${button.value}" target="_blank">${button.value}</a>`
        } else if (button.type === "call") {
          htmlContent += `Call ${button.value}`
        }
        htmlContent += `</li>`
      })
      htmlContent += `</ul>`
    }

    htmlContent += `
      </div>
    </body>
    </html>
  `

    const blob = new Blob([htmlContent], { type: "application/msword" }) // Using application/msword to hint Word
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `campaign_message_${campaign.title.replace(/\s+/g, "_").toLowerCase()}.doc`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success("Campaign message downloaded as .doc!")
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Campaign Details: {campaign.title}
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ p: 2 }}>
          {/* Rejection notice */}
          {isRejected && (
            <Alert severity="error" sx={{ mb: 3 }}>
              This campaign has been rejected. Reason: {campaign.rejectionReason || "No reason provided"}
            </Alert>
          )}
          {/* Running notice */}
          {campaign.isRunning && (
            <Alert severity="success" sx={{ mb: 3 }}>
              This campaign is currently running. Messages are being sent to recipients.
              {campaign.runningAt && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Running since: {new Date(campaign.runningAt).toLocaleString()}
                </Typography>
              )}
            </Alert>
          )}
          {/* Conditional rendering of Campaign Processing section */}
          {showProcessingTimeline ? (
            <Box sx={{ position: "relative", ml: 2, mb: 4 }}>
              {/* Campaign Processing Header */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Typography variant="h6" component="div">
                  Campaign Processing
                </Typography>
              </Box>

              {/* Step 1: Filter non-WhatsApp - Automatic */}
              <Box sx={{ display: "flex", mb: 4, position: "relative" }}>
                {/* Vertical line */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: 24,
                    bottom: -16, // Extend line downwards
                    width: 2,
                    bgcolor: "#4caf50",
                  }}
                />

                {/* Step icon */}
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: "#4caf50",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    mr: 2,
                  }}
                >
                  <FilterAltIcon fontSize="small" />
                </Box>

                {/* Step content */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "medium", color: "#4caf50" }}>
                    Filter non-WhatsApp
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Filter out non-WhatsApp numbers by checking in existing collection
                  </Typography>

                  {/* Status indicator - always completed (as per flow description) */}
                  <Box sx={{ mt: 1 }}>
                    <Chip label="Completed" size="small" color="success" icon={<CheckCircleIcon />} />
                  </Box>
                </Box>
              </Box>

              {/* Step 2: Check whitelist - Automatic */}
              <Box sx={{ display: "flex", mb: 4, position: "relative" }}>
                {/* Vertical line */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: 24,
                    bottom: -16, // Extend line downwards
                    width: 2,
                    bgcolor: "#4caf50",
                  }}
                />

                {/* Step icon */}
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: "#4caf50",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    mr: 2,
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </Box>

                {/* Step content */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "medium", color: "#4caf50" }}>
                    Check whitelist
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Check numbers against whitelisted collection
                  </Typography>

                  {/* Status indicator - always completed (as per flow description) */}
                  <Box sx={{ mt: 1 }}>
                    <Chip label="Completed" size="small" color="success" icon={<CheckCircleIcon />} />
                  </Box>
                </Box>
              </Box>

              {/* Step 3: Find non-WhatsApp - Manual */}
              <Box sx={{ display: "flex", mb: 4, position: "relative" }}>
                {/* Vertical line */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: 24,
                    bottom: -16, // Extend line downwards
                    width: 2,
                    bgcolor: isFindNonWhatsAppCompleted ? "#4caf50" : "#e0e0e0",
                  }}
                />
                {/* Step icon */}
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: isFindNonWhatsAppCompleted ? "#4caf50" : "#e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    mr: 2,
                  }}
                >
                  <FilterAltIcon fontSize="small" />
                </Box>

                {/* Step content */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "medium",
                      color: isFindNonWhatsAppCompleted ? "#4caf50" : "text.primary",
                    }}
                  >
                    Find non-WhatsApp
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Find non-WhatsApp numbers using WhatsApp server
                  </Typography>

                  {/* Action buttons - only shown when validation is not started and step3 is not completed */}
                  {shouldShowFilterButtons && (
                    <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={isSubmitting ? <CircularProgress size={16} /> : <FilterAltIcon />}
                        onClick={onOpenFindNonWhatsAppDialog}
                        disabled={isSubmitting}
                        sx={{ bgcolor: "#4caf50", "&:hover": { bgcolor: "#388e3c" } }}
                      >
                        Filter
                      </Button>
                      <Button variant="outlined" size="small" onClick={onSkipFiltering} disabled={isSubmitting}>
                        Skip
                      </Button>
                    </Box>
                  )}

                  {/* Show validation status if in progress or completed */}
                  {validationStatus && (
                    <Box sx={{ mt: 1 }}>
                      {validationStatus.status === "processing" && (
                        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                          <Typography variant="body2" color="primary" sx={{ mr: 1 }}>
                            Processing: {validationStatus.processedNumbers || 0}/{validationStatus.totalNumbers || 0}
                          </Typography>
                          <CircularProgress size={16} />
                        </Box>
                      )}
                      {validationStatus.status === "completed" && (
                        <Box>
                          <Chip
                            label="Completed"
                            size="small"
                            color="success"
                            icon={<CheckCircleIcon fontSize="small" />}
                          />
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Found {validationStatus.invalidNumbers || 0} non-WhatsApp numbers
                            {serverName && (
                              <Box
                                component="span"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "text.secondary",
                                  fontSize: "0.85rem",
                                  mt: 0.5,
                                }}
                              >
                                <StorageIcon fontSize="small" sx={{ mr: 0.5 }} />
                                Processed by server: {serverName}
                              </Box>
                            )}
                          </Typography>
                        </Box>
                      )}
                      {validationStatus.status === "error" && (
                        <Box>
                          <Chip label="Error" size="small" color="error" icon={<ErrorIcon fontSize="small" />} />
                          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {validationStatus.error || "An error occurred during validation"}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Show step3 completed status (if skipped or completed without validationBatchId) */}
                  {campaign.filteringStatus === "step3_completed" && !campaign.validationBatchId && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label="Completed"
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon fontSize="small" />}
                      />
                      {campaign.removedNonWhatsappNumbers > 0 && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Found {campaign.removedNonWhatsappNumbers} non-WhatsApp numbers
                          {serverName && (
                            <Box
                              component="span"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: "text.secondary",
                                fontSize: "0.85rem",
                                mt: 0.5,
                              }}
                            >
                              <StorageIcon fontSize="small" sx={{ mr: 0.5 }} />
                              Processed by server: {serverName}
                            </Box>
                          )}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Show processing indicator if validation has started but status not yet fetched */}
                  {(campaign.validationBatchId && !validationStatus && userInfo?.token) ||
                    campaign.filteringStatus === "step3_in_progress" ? (
                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <Typography variant="body2" color="primary" sx={{ mr: 1 }}>
                        {campaign.filteringStatus === "step3_in_progress"
                          ? "Finding non-WhatsApp numbers..."
                          : "Starting validation..."}
                      </Typography>
                      <CircularProgress size={16} />
                    </Box>
                  ) : null}
                </Box>
              </Box>

              {/* Step 4: Compare with Previous Campaigns - Manual */}
              <Box sx={{ display: "flex", mb: 2, position: "relative" }}>
                {/* Step icon */}
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: isComparisonCompletedOrSkipped ? "#4caf50" : "#e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    mr: 2,
                  }}
                >
                  <CompareArrowsIcon fontSize="small" />
                </Box>

                {/* Step content */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "medium",
                      color: isComparisonCompletedOrSkipped ? "#4caf50" : "text.primary",
                    }}
                  >
                    Compare with Previous Campaigns
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Find common numbers with previously completed campaigns
                  </Typography>

                  {shouldShowCompareButtons && (
                    <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={isComparing ? <CircularProgress size={16} /> : <CompareArrowsIcon />}
                        onClick={handleOpenCompareCampaignsDialog}
                        disabled={isComparing || campaignLoading || isSubmitting}
                        sx={{ bgcolor: "#2196f3", "&:hover": { bgcolor: "#1976d2" } }}
                      >
                        Compare
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSkipComparison}
                        disabled={isComparing || campaignLoading || isSubmitting}
                      >
                        Skip
                      </Button>
                    </Box>
                  )}

                  {/* Show comparison status */}
                  {campaign.comparisonStatus === "in_progress" && (
                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <Typography variant="body2" color="primary" sx={{ mr: 1 }}>
                        Comparing campaigns...
                      </Typography>
                      <CircularProgress size={16} />
                    </Box>
                  )}
                  {campaign.comparisonStatus === "completed" && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label="Completed"
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon fontSize="small" />}
                      />
                      {campaign.commonNumbersList && campaign.commonNumbersList.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Found {campaign.commonNumbersList.length} common numbers.
                          </Typography>

                          {downloadLink && (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              sx={{ mt: 1 }}
                              href={downloadLink}
                              download={`common_numbers_${campaign.title.substring(0, 6)}.csv`}
                            >
                              Download Common Numbers CSV
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                  {campaign.comparisonStatus === "skipped" && (
                    <Box sx={{ mt: 1 }}>
                      <Chip label="Skipped" size="small" color="info" />
                    </Box>
                  )}
                  {campaign.comparisonStatus === "error" && (
                    <Box sx={{ mt: 1 }}>
                      <Chip label="Error" size="small" color="error" icon={<ErrorIcon fontSize="small" />} />
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {campaign.comparisonError || "An error occurred during comparison"}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Rejection reason display */}
              {isRejected && campaign.rejectionReason && (
                <Box sx={{ mt: 3, mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom color="error">
                    Rejection Reason
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "#ffebee", borderColor: "#ffcdd2" }}>
                    <Typography variant="body2" color="error">
                      {campaign.rejectionReason}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Complete Process button - only shown when validation and comparison are completed/skipped and not rejected (Admin specific) */}
              {userInfo?.user?.role === "admin" &&
                isFindNonWhatsAppCompleted &&
                isComparisonCompletedOrSkipped &&
                !isRejected &&
                campaign.status === "pending" && (
                  <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                      onClick={onComplete}
                      disabled={isSubmitting}
                    >
                      Complete Process
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={onReject}
                      disabled={isSubmitting}
                    >
                      Reject Campaign
                    </Button>
                  </Box>
                )}

              {/* Run Campaign button - only shown for processing campaigns with completed filtering and comparison (Admin specific) */}
              {canRunCampaign && (
                <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={handleOpenRunCampaignDialog}
                    disabled={shouldDisableRunButton}
                    sx={{ bgcolor: "#008069", "&:hover": { bgcolor: "#128C7E" } }}
                  >
                    Select Server to Run Campaign
                  </Button>
                </Box>
              )}

              {/* Campaign Running Status - shown when campaign is running */}
              {campaign.isRunning && (
                <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
                  <Chip
                    label="Campaign is running"
                    color="success"
                    icon={<CircularProgress size={16} sx={{ color: "white !important" }} />}
                    sx={{
                      bgcolor: "#008069",
                      color: "white",
                      animation: "pulse 2s infinite ease-in-out",
                      "@keyframes pulse": {
                        "0%": { opacity: 1 },
                        "50%": { opacity: 0.6 },
                        "100%": { opacity: 1 },
                      },
                    }}
                  />
                </Box>
              )}

              {canFinishCampaign && campaign.channelWaitReason && (
                <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                  <Button
                    onClick={handleOpenFinishCampaignConfirm}
                    color="error"
                    variant="outlined"
                    disabled={isFinishingCampaign}
                    startIcon={isFinishingCampaign ? <CircularProgress size={20} /> : <StopIcon />}
                  >
                    {isFinishingCampaign ? "Finishing..." : "Finish Campaign"}
                  </Button>
                </Box>
              )}

              {/* Reset Campaign button - only shown for rejected campaigns (Admin specific) */}
              {userInfo?.user?.role === "admin" && isRejected && onResetCampaign && (
                <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : <RefreshIcon />}
                    onClick={() => onResetCampaign(campaign._id)}
                    disabled={isSubmitting}
                  >
                    Reset Campaign
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Media Files
                </Typography>
                {campaign.media && campaign.media.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {campaign.media.map((mediaItem, index) => {
                          const mediaUrl = getMediaUrl(mediaItem)
                          const isImage =
                            mediaItem.type?.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp)$/i.test(mediaItem.name)
                          const isVideo =
                            mediaItem.type?.startsWith("video/") || /\.(mp4|webm|ogg)$/i.test(mediaItem.name)
                          const isPdf = mediaItem.type === "application/pdf" || /\.pdf$/i.test(mediaItem.name)

                          return (
                            <TableRow key={index}>
                              <TableCell>{mediaItem.type?.split("/")[0] || "file"}</TableCell>
                              <TableCell>{mediaItem.name}</TableCell>
                              <TableCell>
                                {isImage || isVideo || isPdf ? (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                      handleOpenMediaPreview({
                                        type: mediaItem.type,
                                        url: mediaUrl,
                                        name: mediaItem.name,
                                      })
                                    }
                                    startIcon={
                                      isImage ? <VisibilityIcon /> : isVideo ? <PlayArrowIcon /> : <VisibilityIcon />
                                    }
                                    sx={{ mr: 1 }}
                                  >
                                    View
                                  </Button>
                                ) : null}
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleDownloadMedia(mediaItem)} // Changed to onClick
                                  startIcon={<DownloadIcon />}
                                >
                                  Download
                                </Button>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No media uploaded.
                  </Typography>
                )}
              </Box>
              {/* Campaign Details (always shown) */}

              <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<DescriptionIcon />}
                  onClick={handleDownloadMessageAsDoc}
                  sx={{ bgcolor: "#17a2b8", "&:hover": { bgcolor: "#138496" } }}
                >
                  Download Message (.doc)
                </Button>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Campaign Type
                </Typography>
                <Chip
                  label={`${campaign.campaignType?.toUpperCase() || "NORMAL"} ${campaign.campaignOption?.toUpperCase() || "DOMESTIC"
                    }`}
                  size="small"
                  sx={{
                    bgcolor: campaign.campaignType === "button" ? "#e8eaf6" : "#fff3e0",
                    color: campaign.campaignType === "button" ? "#3f51b5" : "#e65100",
                  }}
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Title
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {campaign.title || "No title"}
                  </Typography>
                </Paper>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Message
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {campaign.message || "No message content"}
                  </Typography>
                </Paper>
              </Box>

              {campaign.campaignType === "button" && campaign.buttons && campaign.buttons.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Buttons
                  </Typography>
                  {campaign.buttons.map((button, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {button.text}
                      </Typography>

                      {button.type === "link" && button.value && (
                        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                          <LinkIcon sx={{ mr: 1, fontSize: 20, color: "#1976d2" }} />
                          <a
                            href={button.value.startsWith("http") ? button.value : `https://${button.value}`}
                            target="_blank"
                            rel="noreferrer noopener"
                            style={{ fontWeight: "medium", fontSize: "1rem", color: "#1976d2", textDecoration: "none" }}
                          >
                            {button.value}
                          </a>
                        </Box>
                      )}

                      {button.type === "call" && button.value && (
                        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                          <PhoneIcon sx={{ mr: 1, fontSize: 20, color: "#388e3c" }} />
                          <a
                            href={`tel:${button.value}`}
                            style={{ fontWeight: "medium", fontSize: "1rem", color: "#388e3c", textDecoration: "none" }}
                          >
                            {button.value}
                          </a>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              {campaign.numbers && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ mb: 0, mr: 1 }}>
                      Numbers
                    </Typography>
                    <Chip label={`Total: ${campaign.originalNumbers.length || 0}`} color="default" />
                    {userInfo?.user?.role === "admin" && campaign.originalNumbers.length > 0 && (
                      <IconButton size="small" onClick={handleDownloadTotalNumbers} sx={{ ml: 0.5 }}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  {userInfo?.user?.role === "admin" && (
                    <>
                      <Box sx={{ display: "flex", gap: 2, mb: 2, mt: 2 }}>
                        {campaign.invalidNumbers > 0 && (
                          <Chip label={`Invalid: ${campaign.invalidNumbers}`} color="error" />
                        )}
                        {campaign.duplicateNumbers > 0 && (
                          <Chip label={`Duplicate: ${campaign.duplicateNumbers}`} color="warning" />
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                        <Chip
                          label={`Non-WhatsApp: ${campaign.nonWhatsAppNumbers?.length || 0}`}
                          color="error"
                          variant="outlined"
                        />
                        {campaign.nonWhatsAppNumbers?.length > 0 && (
                          <IconButton size="small" onClick={handleDownloadNonWhatsAppNumbers} sx={{ ml: -1.5 }}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        )}
                        <Chip label={`WhatsApp: ${campaign.numbers?.length || 0}`} color="success" variant="outlined" />
                        {campaign.numbers?.length > 0 && (
                          <IconButton size="small" onClick={handleDownloadWhatsAppNumbers} sx={{ ml: -1.5 }}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        )}

                        <Chip
                          label={`Whitelisted & Common: ${campaign?.commonAndWhitelistedListCount || 0}`}
                          color="info"
                          variant="outlined"
                        />

                        <IconButton size="small" onClick={handleDownloadWhitelistedAndCommonNumbers} sx={{ ml: -1.5 }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        {/* <Chip
                          label={`Common: ${campaign.commonNumbersList?.length || 0}`}
                          color="primary"
                          variant="outlined"
                        />
                        {campaign.commonNumbersList?.length > 0 && (
                          <IconButton size="small" onClick={handleDownloadCommonNumbers} sx={{ ml: -1.5 }}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        )} */}

                        {/* <Chip
                          label={`WhiteList without common: ${campaign.whitelistedFoundNumbers || 0}`}
                          color="primary"
                          variant="outlined"
                        />
                        {campaign.whitelistedFoundNumbers > 0 && (
                          <IconButton size="small" onClick={handleDownloadWhiteListWithoutCommonNumbers} sx={{ ml: -1.5 }}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        )} */}
                      </Box>
                    </>
                  )}
                </Box>
              )}
              {/* {campaign.validNumbersList && campaign.validNumbersList.length > 0 && userInfo.user.role === "admin" && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Valid Numbers
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: "auto", bgcolor: "#f5f5f5" }}>
                    <Typography variant="body2" component="div">
                      {campaign.validNumbersList.slice(0, 10).map((number, index) => (
                        <Box key={index} component="span" sx={{ display: "block" }}>
                          {number}
                        </Box>
                      ))}
                      {campaign.validNumbersList.length > 50 && (
                        <Box sx={{ mt: 1, fontStyle: "italic", color: "text.secondary" }}>
                          ...and {campaign.validNumbersList.length - 50} more
                        </Box>
                      )}
                    </Typography>
                  </Paper>
                </Box>
              )} */}
              {/* {campaign.invalidNumbersList &&
                campaign.invalidNumbersList.length > 0 &&
                userInfo.user.role === "admin" && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Invalid Numbers
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: "auto", bgcolor: "#f5f5f5" }}>
                      <Typography variant="body2" component="div">
                        {campaign.invalidNumbersList.slice(0, 50).map((number, index) => (
                          <Box key={index} component="span" sx={{ display: "block" }}>
                            {number}
                          </Box>
                        ))}
                        {campaign.invalidNumbersList.length > 50 && (
                          <Box sx={{ mt: 1, fontStyle: "italic", color: "text.secondary" }}>
                            ...and {campaign.invalidNumbersList.length - 50} more
                          </Box>
                        )}
                      </Typography>
                    </Paper>
                  </Box>
                )} */}
              {campaign.duplicateNumbersList &&
                campaign.duplicateNumbersList.length > 0 &&
                userInfo.user.role === "admin" && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Duplicate Numbers
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: "auto", bgcolor: "#f5f5f5" }}>
                      <Typography variant="body2" component="div">
                        {campaign.duplicateNumbersList.slice(0, 50).map((number, index) => (
                          <Box key={index} component="span" sx={{ display: "block" }}>
                            {number}
                          </Box>
                        ))}
                        {campaign.duplicateNumbersList.length > 50 && (
                          <Box sx={{ mt: 1, fontStyle: "italic", color: "text.secondary" }}>
                            ...and {campaign.duplicateNumbersList.length - 50} more
                          </Box>
                        )}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              {campaign.status === "rejected" && campaign.rejectionReason && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom color="error">
                    Rejection Reason
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "#ffebee", borderColor: "#ffcdd2" }}>
                    <Typography variant="body2" color="error">
                      {campaign.rejectionReason}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      {/* Main Dialog Actions */}
      <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Run Campaign Dialog */}
      <Dialog open={runCampaignDialogOpen} onClose={handleCloseRunCampaignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Select WhatsApp Server to Run Campaign
          <IconButton
            aria-label="close"
            onClick={handleCloseRunCampaignDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select a WhatsApp server to run this campaign. This server will be used to send messages to the recipients.
          </DialogContentText>

          {/* Campaign Statistics */}
          <Box sx={{ mb: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1, bgcolor: "#f9f9f9" }}>
            <Typography variant="subtitle1" gutterBottom>
              Campaign Statistics
            </Typography>
            <Typography variant="body2">
              <strong>Total Numbers:</strong> {campaign.totalNumbers || 0}
            </Typography>
            <Typography variant="body2">
              <strong>Common Number:</strong> {campaign.commonNumbersList?.length || 0}
            </Typography>
            <Typography variant="body2">
              <strong>White Listing:</strong> {campaign.whitelistedFoundNumbers || 0}
            </Typography>
            <Typography variant="body2">
              <strong>Non Whatsapp Old:</strong> {campaign.removedNonWhatsappNumbers || 0}
            </Typography>
            <Typography variant="body2">
              <strong>Non Whatsapp New:</strong>{" "}
              {validationStatus?.status === "completed" ? validationStatus.invalidNumbers : 0}
            </Typography>
            {/* <Typography variant="body2">
              <strong>Filter Later On:</strong> {campaign.filterLaterOn ? "True" : "False"}
            </Typography> */}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Note: Campaigns added through API's, whatsappworld's whitelisting wouldn't work.
            </Typography>
          </Box>

          {/* Run Type Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Run Type
            </Typography>
            <ToggleButtonGroup
              value={runType}
              exclusive
              onChange={(event, newRunType) => {
                if (newRunType !== null) {
                  setRunType(newRunType)
                }
              }}
              aria-label="run type"
              fullWidth
            >
              <ToggleButton value="all" aria-label="all numbers">
                All
              </ToggleButton>
              <ToggleButton value="whitelist" aria-label="white listing only">
                White Listing Only
              </ToggleButton>
              <ToggleButton value="whiteAndCommon" aria-label="white and common numbers only">
                White & Common Numbers Only
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Cutting Percentage */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Cutting Percentage
            </Typography>
            <TextField
              type="number"
              value={cuttingPercentage}
              onChange={(e) => {
                const value = Number.parseInt(e.target.value, 10)
                setCuttingPercentage(isNaN(value) ? 0 : Math.max(0, Math.min(100, value)))
              }}
              fullWidth
              size="small"
              label="Percentage (0-100)"
              inputProps={{ min: 0, max: 100 }}
            />
          </Box>

          {/* Server Selection */}
          {loadingServers ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
              <CircularProgress />
            </Box>
          ) : servers.length > 0 ? (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="server-select-label">WhatsApp Server</InputLabel>
              <Select
                labelId="server-select-label"
                value={selectedServerId}
                onChange={(e) => setSelectedServerId(e.target.value)}
                label="WhatsApp Server"
              >
                {servers.map((server) => (
                  <MenuItem key={server._id} value={server._id}>
                    {server.name} - {server.description || server.url}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography color="error" sx={{ my: 2 }}>
              No active WhatsApp servers found. Please add servers in the admin panel.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          {canFinishCampaign && (
            <Button
              onClick={handleOpenFinishCampaignConfirm}
              color="error"
              variant="outlined"
              disabled={isFinishingCampaign}
              startIcon={isFinishingCampaign ? <CircularProgress size={20} /> : <StopIcon />}
            >
              {isFinishingCampaign ? "Finishing..." : "Finish Campaign"}
            </Button>
          )}
          <Box>
            <Button onClick={handleCloseRunCampaignDialog}>Cancel</Button>
            {
              campaign.campaignType !== "button" && (
                <Button
                  onClick={handleRunCampaign}
                  color="primary"
                  variant="contained"
                  disabled={runningCampaignAction || servers.length === 0 || !selectedServerId}
                  startIcon={runningCampaignAction ? <CircularProgress size={20} /> : <SendIcon />}
                  sx={{ bgcolor: "#008069", "&:hover": { bgcolor: "#128C7E" } }}
                >
                  {runningCampaignAction ? "Running..." : "Start"}
                </Button>
              )
            }

          </Box>
        </DialogActions>
      </Dialog>

      {/* NEW: Finish Campaign Confirmation Dialog */}
      <Dialog
        open={finishCampaignConfirmOpen}
        onClose={handleCloseFinishCampaignConfirm}
        aria-labelledby="finish-campaign-dialog-title"
        aria-describedby="finish-campaign-dialog-description"
      >
        <DialogTitle id="finish-campaign-dialog-title">Confirm Finish Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText id="finish-campaign-dialog-description">
            Are you sure you want to finish this campaign? This will stop all ongoing message sending for this campaign
            and clear its message queue. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFinishCampaignConfirm} disabled={isFinishingCampaign}>
            Cancel
          </Button>
          <Button
            onClick={confirmFinishCampaign}
            color="error"
            variant="contained"
            disabled={isFinishingCampaign}
            startIcon={isFinishingCampaign ? <CircularProgress size={20} /> : <StopIcon />}
          >
            {isFinishingCampaign ? "Finishing..." : "Confirm Finish"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compare Campaigns Dialog */}
      <CompareCampaignsDialog
        open={compareCampaignDialogOpen}
        onClose={handleCloseCompareCampaignsDialog}
        onCompare={handleCompareCampaigns}
        currentCampaignId={campaign._id}
        currentCampaignUserId={campaign.userId._id || campaign.userId}
        isComparing={isComparing}
        userInfo={userInfo}
      />

      {/* Media Preview Dialog */}
      <Dialog open={mediaPreviewDialogOpen} onClose={handleCloseMediaPreview} maxWidth="lg" fullWidth maxHeight="100vh">
        <DialogTitle>
          Media Preview
          <IconButton
            aria-label="close"
            onClick={handleCloseMediaPreview}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300, bgcolor: "black" }}
        >
          {mediaPreviewContent?.type?.startsWith("image") && (
            <img
              src={mediaPreviewContent.url || `${process.env.REACT_APP_API_URL}/placeholder.svg`}
              alt={mediaPreviewContent.name || "Media Preview"}
              style={{ maxWidth: "100%", maxHeight: "calc(100vh - 150px)", objectFit: "contain" }}
              onError={(e) => {
                console.error(`Image failed to load in preview: ${mediaPreviewContent.url}`)
                e.target.onerror = null
                e.target.src = `${process.env.REACT_APP_API_URL}/placeholder.svg`
              }}
            />
          )}
          {mediaPreviewContent?.type?.startsWith("video") && (
            <video
              controls
              src={mediaPreviewContent.url}
              style={{ maxWidth: "100%", maxHeight: "calc(100vh - 150px)" }}
            >
              Your browser does not support the video tag.
            </video>
          )}
          {mediaPreviewContent?.type === "pdf" && (
            <iframe
              src={mediaPreviewContent.url}
              width="100%"
              height="600px"
              style={{ border: "none" }}
              title="PDF Preview"
            >
              This browser does not support PDFs. Please download the PDF to view it.
              <a href={mediaPreviewContent.url} download={mediaPreviewContent.name}>
                Download PDF
              </a>
            </iframe>
          )}
          {!mediaPreviewContent && <Typography color="text.secondary">No media to preview.</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMediaPreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default CampaignDetailsDialog
